import { getScrollEventTarget, getScrollTop, scrollTo } from '@theme/scroll-container';

class ShokzNavigation extends HTMLElement {
  constructor() {
    super();
    this.toggleMenu = this.toggleMenu.bind(this);
    this.syncNavigation = this.syncNavigation.bind(this);
    this.handleNavigationClick = this.handleNavigationClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('[data-shokz-menu-toggle]');
    this.navigation = this.querySelector('[data-shokz-navigation]');
    // The header now renders once, globally, via the header-group (it's no
    // longer nested inside the page hero), so the hero must be looked up on
    // the page instead of via ancestry.
    this.hero = document.querySelector('[data-shokz-page-hero]');
    this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');
    this.backToTopButton = this.querySelector('[data-shokz-back-to-top]');

    this.button?.addEventListener('click', this.toggleMenu);
    this.navigation?.addEventListener('click', this.handleNavigationClick);
    this.backToTopButton?.addEventListener('click', this.scrollToTop);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleScroll, { passive: true });

    if (this.mobileMediaQuery.addEventListener) {
      this.mobileMediaQuery.addEventListener('change', this.syncNavigation);
    } else {
      this.mobileMediaQuery.addListener(this.syncNavigation);
    }

    this.syncNavigation();
    this.syncStickyState();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.toggleMenu);
    this.navigation?.removeEventListener('click', this.handleNavigationClick);
    this.backToTopButton?.removeEventListener('click', this.scrollToTop);
    document.removeEventListener('keydown', this.handleKeydown);
    this.scrollEventTarget?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);

    if (this.mobileMediaQuery?.removeEventListener) {
      this.mobileMediaQuery.removeEventListener('change', this.syncNavigation);
    } else {
      this.mobileMediaQuery?.removeListener(this.syncNavigation);
    }

    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
  }

  toggleMenu() {
    const isOpen = this.button?.getAttribute('aria-expanded') === 'true';
    this.setOpen(!isOpen);
  }

  setOpen(isOpen) {
    this.button?.setAttribute('aria-expanded', String(isOpen));
    this.navigation?.setAttribute('aria-hidden', String(!isOpen));
    this.classList.toggle('is-open', isOpen);
    document.body.classList.toggle('shokz-menu-open', isOpen);
  }

  syncNavigation() {
    this.syncScrollEventTarget();

    if (this.mobileMediaQuery.matches) {
      this.setOpen(false);
    } else {
      this.button?.setAttribute('aria-expanded', 'false');
      this.navigation?.setAttribute('aria-hidden', 'false');
      this.classList.remove('is-open');
      document.body.classList.remove('shokz-menu-open');
    }

    this.syncStickyState();
  }

  syncScrollEventTarget() {
    const nextScrollEventTarget = getScrollEventTarget();
    if (nextScrollEventTarget === this.scrollEventTarget) return;

    this.scrollEventTarget?.removeEventListener('scroll', this.handleScroll);
    this.scrollEventTarget = nextScrollEventTarget;
    this.scrollEventTarget.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  handleScroll() {
    if (this.animationFrame) return;

    this.animationFrame = window.requestAnimationFrame(() => {
      this.animationFrame = null;
      this.syncStickyState();
    });
  }

  syncStickyState() {
    // Switch to the solid (white background) state as soon as the page starts
    // scrolling, instead of waiting for the whole hero to scroll past. This
    // avoids the transparent nav overlapping the hero's own heading/eyebrow text.
    const isSticky = getScrollTop() > 0;

    // "Back to top" should reveal once the equivalent of a hero has scrolled
    // out of view. Pages with a dedicated hero (`data-shokz-page-hero`) use its
    // actual bottom edge; pages without one (e.g. generic content pages using
    // the solid header) fall back to a one-viewport-height scroll distance.
    const isPastHero = this.hero
      ? this.hero.getBoundingClientRect().bottom <= 0
      : getScrollTop() > window.innerHeight;

    this.classList.toggle('is-sticky', isSticky);
    this.setAttribute('data-sticky', String(isSticky));
    this.setAttribute('data-past-hero', String(isPastHero));
  }

  handleNavigationClick(event) {
    const selectedLink = event.target.closest('.shokz-navigation__link, .shokz-navigation__contact');
    if (!selectedLink) return;

    if (this.mobileMediaQuery.matches) this.setOpen(false);
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.button?.getAttribute('aria-expanded') === 'true') {
      this.setOpen(false);
      this.button.focus();
    }
  }

  scrollToTop() {
    scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('shokz-navigation')) {
  customElements.define('shokz-navigation', ShokzNavigation);
}
